/**
 * Multi-Contaminant Plume Utilities
 *
 * Provides calculations for compound-specific plume lengths based on
 * physicochemical properties (retardation, biodegradation, solubility).
 *
 * Research basis:
 * - Each contaminant forms its own distinct plume (not one combined plume)
 * - Plume lengths vary based on retardation factor and biodegradation rate
 * - MTBE extends furthest (low sorption, slow biodegradation)
 * - Toluene extends shortest (fast biodegradation)
 */

import { COMPOUND_PARAMETERS, CompoundPlumeParameters, ContaminantResult } from '../../api/polygon';

// ============================================================================
// COMPOUND PROPERTY LOOKUP
// ============================================================================

/**
 * Default compound properties if not found in COMPOUND_PARAMETERS.
 */
const DEFAULT_COMPOUND_PROPERTIES: CompoundPlumeParameters = {
  compound: 'Unknown',
  molecular_weight: 100,
  solubility_mg_l: 1000,
  log_kow: 2.0,
  log_koc: 2.0,
  koc_l_kg: 100,
  henry_constant: 0.1,
  vapor_pressure_mmhg: 10,
  half_life_days: 365,
  half_life_aerobic_days: 180,
  half_life_anaerobic_days: 730,
  retardation_factor: 2.0,
  relative_plume_length: 0.8,
  plume_color: '#9ca3af',  // Gray for unknown compounds
};

// ============================================================================
// SITE-SPECIFIC PARAMETERS FOR RETARDATION CALCULATION
// ============================================================================

/**
 * Default aquifer parameters for retardation factor calculation.
 * R = 1 + (ρ_b / n) × K_oc × f_oc
 *
 * Where:
 *   ρ_b = bulk density (g/cm³)
 *   n = effective porosity (dimensionless)
 *   K_oc = organic carbon partition coefficient (L/kg)
 *   f_oc = fraction organic carbon (dimensionless)
 */
export interface AquiferParameters {
  bulk_density_g_cm3: number;     // ρ_b, typically 1.5-1.9
  effective_porosity: number;      // n, typically 0.20-0.40
  fraction_organic_carbon: number; // f_oc, typically 0.0001-0.01
}

export const DEFAULT_AQUIFER_PARAMS: AquiferParameters = {
  bulk_density_g_cm3: 1.7,       // Sandy aquifer
  effective_porosity: 0.30,       // Medium sand
  fraction_organic_carbon: 0.001, // Low organic content
};

/**
 * Calculate retardation factor for a compound given site-specific parameters.
 * R = 1 + (ρ_b / n) × K_oc × f_oc
 */
export function calculateRetardationFactor(
  koc_l_kg: number,
  aquiferParams: AquiferParameters = DEFAULT_AQUIFER_PARAMS
): number {
  const { bulk_density_g_cm3, effective_porosity, fraction_organic_carbon } = aquiferParams;

  // Convert K_oc from L/kg to cm³/g (multiply by 1, same units)
  // R = 1 + (ρ_b / n) × K_oc × f_oc
  const R = 1 + (bulk_density_g_cm3 / effective_porosity) * koc_l_kg * fraction_organic_carbon;

  return Math.max(1.0, R); // R must be >= 1
}

/**
 * Compound name aliases for matching.
 */
const COMPOUND_ALIASES: Record<string, string> = {
  // Individual BTEX
  'benzene': 'Benzene',
  'toluene': 'Toluene',
  'ethylbenzene': 'Ethylbenzene',
  'ethyl benzene': 'Ethylbenzene',
  'xylenes': 'Xylenes',
  'xylene': 'Xylenes',
  'total xylenes': 'Xylenes',
  'm,p-xylenes': 'Xylenes',
  'o-xylene': 'Xylenes',
  // Aggregates
  'total btex': 'Total BTEX',
  'btex': 'Total BTEX',
  'total tph': 'Total TPH',
  'tph': 'Total TPH',
  'total petroleum hydrocarbons': 'Total TPH',
  // Carbon ranges — SQL Server naming variants
  'c6-c12': 'C6-C12',
  'c6-c12 gasoline range hydrocarbons': 'C6-C12',
  'c6_c12': 'C6-C12',
  'c12-c28': 'C12-C28',
  'c12-c28 diesel range hydrocarbons': 'C12-C28',
  'c12_c28': 'C12-C28',
  'c12-28': 'C12-C28',
  'c28-c35': 'C28-C35',
  'c28-c35 motor oil range hydrocarbons': 'C28-C35',
  'c28_c35': 'C28-C35',
  'c28-35': 'C28-C35',
};

/**
 * Get compound properties by name.
 * Handles case-insensitive matching and common aliases.
 */
export function getCompoundProperties(compoundName: string): CompoundPlumeParameters {
  const normalized = compoundName.toLowerCase().trim();

  // Check aliases first (exact match)
  const aliasMatch = COMPOUND_ALIASES[normalized];
  if (aliasMatch) {
    const props = COMPOUND_PARAMETERS.find(p => p.compound === aliasMatch);
    if (props) return props;
  }

  // Try direct match (case-insensitive)
  const directMatch = COMPOUND_PARAMETERS.find(
    p => p.compound.toLowerCase() === normalized
  );
  if (directMatch) return directMatch;

  // Substring match on aliases (handles SQL Server names like "C6-C12 Gasoline Range Hydrocarbons")
  for (const [alias, canonical] of Object.entries(COMPOUND_ALIASES)) {
    if (normalized.includes(alias)) {
      const props = COMPOUND_PARAMETERS.find(p => p.compound === canonical);
      if (props) return props;
    }
  }

  // Return default properties
  return { ...DEFAULT_COMPOUND_PROPERTIES, compound: compoundName };
}

// ============================================================================
// PLUME LENGTH CALCULATIONS
// ============================================================================

/**
 * Calculate relative plume length factor based on compound properties.
 *
 * Based on the Domenico (1987) analytical solution for 1D advection-dispersion
 * with first-order decay. The plume length estimation formula:
 *
 *   L = (v_c / λ) × ln(C_source / C_target)
 *
 * Where:
 *   v_c = contaminant velocity = v_gw / R (groundwater velocity / retardation)
 *   λ = first-order decay rate = ln(2) / half_life (compound-specific)
 *   C_source = source concentration
 *   C_target = target concentration (e.g., MCL)
 *
 * Relative to benzene:
 *   L_relative = (R_benzene / R_compound) × (t½_compound / t½_benzene)
 *
 * Key physics:
 *   - Lower retardation → faster transport → longer plume
 *   - Longer half-life → slower decay → longer plume
 *
 * Expected results from technical specification:
 *   - MTBE: 2-10x benzene (low sorption, very slow biodegradation)
 *   - EDB: 2-5x benzene (no biodegradation outside BTEX zone)
 *   - Benzene: 1.0 (reference compound)
 *   - Toluene: 0.5-0.8x (very fast biodegradation)
 *   - Ethylbenzene: 0.3-0.6x (moderate biodeg, high retardation)
 *   - Xylenes: 0.2-0.5x (moderate biodeg, highest retardation in BTEX)
 *   - Naphthalene: 0.1-0.2x (very high retardation)
 */
export function calculateRelativePlumeLength(
  compound: CompoundPlumeParameters,
  aquiferParams: AquiferParameters = DEFAULT_AQUIFER_PARAMS
): number {
  // If compound has pre-calculated relative_plume_length, use it
  if (compound.relative_plume_length !== undefined && compound.relative_plume_length > 0) {
    return compound.relative_plume_length;
  }

  const benzeneProps = COMPOUND_PARAMETERS.find(p => p.compound === 'Benzene');
  if (!benzeneProps) return 1.0;

  // Calculate site-specific retardation factors
  const R_ref = calculateRetardationFactor(benzeneProps.koc_l_kg, aquiferParams);
  const R = calculateRetardationFactor(compound.koc_l_kg, aquiferParams);

  // Get half-lives
  const halfLife_ref = benzeneProps.half_life_days;
  const halfLife = compound.half_life_days;

  // Handle infinite half-life (no biodegradation)
  // Use a large value to represent very long persistence
  const effectiveHalfLife = halfLife === Infinity ? 10000 : halfLife;
  const effectiveHalfLife_ref = halfLife_ref === Infinity ? 10000 : halfLife_ref;

  // ==========================================================================
  // RETARDATION EFFECT
  // ==========================================================================
  // Lower R = faster contaminant velocity = longer plume
  // v_contaminant = v_groundwater / R
  const retardationEffect = R_ref / R;

  // ==========================================================================
  // BIODEGRADATION EFFECT
  // ==========================================================================
  // Longer half-life = slower decay = longer plume
  // L ∝ t½ for first-order decay
  const decayEffect = effectiveHalfLife / effectiveHalfLife_ref;

  // ==========================================================================
  // COMBINED RELATIVE LENGTH
  // ==========================================================================
  // L_relative = (R_ref / R) × (t½ / t½_ref)
  const relativeLengthRaw = retardationEffect * decayEffect;

  // Apply physical constraints:
  // - Minimum 0.10x benzene (high-retardation compounds like naphthalene)
  // - Maximum 10.0x benzene (MTBE in favorable conditions)
  const relativeLength = Math.max(0.10, Math.min(10.0, relativeLengthRaw));

  return relativeLength;
}

/**
 * Calculate compound-specific plume length given a reference dissolved plume length.
 *
 * @param referenceLengthFt - Base plume length (typically for primary contaminant)
 * @param compound - Compound properties
 * @param referenceCompound - Reference compound (default: Benzene)
 */
export function calculateCompoundPlumeLength(
  referenceLengthFt: number,
  compound: CompoundPlumeParameters,
  referenceCompound: string = 'Benzene'
): number {
  // Get reference compound's relative length
  const refProps = getCompoundProperties(referenceCompound);
  const refRelative = calculateRelativePlumeLength(refProps);

  // Get target compound's relative length
  const targetRelative = calculateRelativePlumeLength(compound);

  // Calculate adjusted length
  const adjustedLength = referenceLengthFt * (targetRelative / refRelative);

  return Math.round(adjustedLength);
}

// ============================================================================
// MULTI-CONTAMINANT PLUME GENERATION
// ============================================================================

/**
 * Compound plume data for visualization.
 */
export interface CompoundPlumeData {
  compound: string;
  color: string;
  lengthFt: number;
  widthFt: number;
  concentration_ug_l: number;
  mcl_ug_l?: number;
  exceedsMcl: boolean;
  relativeLength: number;  // 1.0 = benzene reference
  retardationFactor: number;
  halfLifeDays: number;
  // NEW: Actual polygon from well measurement triangulation
  polygon?: [number, number][];  // [[lat, lon], ...] - from measured well data
  sourceType?: 'measured' | 'theoretical';  // How the plume shape was determined
  // Wells with detections (above threshold) - used for concentration interpolation
  wells?: Array<{ lat: number; lon: number; concentration: number; well_id?: string }>;
  // Wells below threshold (clean wells) - plume particles should avoid these exclusion zones
  wells_below_threshold?: Array<{ lat: number; lon: number; well_id?: string }>;
}

/**
 * Generate plume data for all detected contaminants.
 *
 * @param contaminantResults - Per-contaminant results from simulation
 * @param baseLengthFt - Base dissolved plume length (from primary contaminant)
 * @param baseWidthFt - Base dissolved plume width
 * @param primaryContaminant - Primary contaminant used for base dimensions
 */
export function generateMultiContaminantPlumes(
  contaminantResults: ContaminantResult[] | undefined,
  baseLengthFt: number,
  baseWidthFt: number,
  primaryContaminant: string = 'Benzene',
  selectedTimeIndex: number = 0
): CompoundPlumeData[] {
  if (!contaminantResults || contaminantResults.length === 0) {
    return [];
  }

  // Get primary compound properties for reference (used only as fallback)
  const primaryProps = getCompoundProperties(primaryContaminant);
  const primaryRelative = calculateRelativePlumeLength(primaryProps);

  // Generate plume data for each contaminant
  // PRIORITY 1: Use actual polygon data from plume_states (measured from well samples)
  // PRIORITY 2: Fall back to theoretical scaling from compound properties
  const plumes: CompoundPlumeData[] = contaminantResults.map(result => {
    const props = getCompoundProperties(result.contaminant_type);

    // Check if this contaminant has actual polygon data from well measurements
    const plumeState = result.plume_states?.[selectedTimeIndex];
    const actualPolygon = plumeState?.dissolved?.polygon;
    const actualLengthFt = plumeState?.dissolved?.length_ft;
    const actualWidthFt = plumeState?.dissolved?.width_ft;
    const actualAreaSqft = plumeState?.dissolved?.area_sqft;

    let lengthFt: number;
    let widthFt: number;
    let polygon: [number, number][] | undefined;
    let sourceType: 'measured' | 'theoretical';

    if (actualPolygon && actualPolygon.length >= 3) {
      // USE ACTUAL MEASURED POLYGON - from well sample triangulation
      lengthFt = actualLengthFt || baseLengthFt;
      widthFt = actualWidthFt || baseWidthFt;
      polygon = actualPolygon as [number, number][];
      sourceType = 'measured';
    } else {
      // FALLBACK: Use theoretical scaling from compound properties
      const relativeLength = calculateRelativePlumeLength(props);
      const lengthFactor = relativeLength / primaryRelative;
      lengthFt = Math.round(baseLengthFt * lengthFactor);
      const widthFactor = Math.sqrt(lengthFactor);
      widthFt = Math.round(baseWidthFt * widthFactor);
      sourceType = 'theoretical';
    }

    return {
      compound: result.contaminant_type,
      color: props.plume_color,
      lengthFt,
      widthFt,
      concentration_ug_l: result.initial_concentration_ug_l,
      mcl_ug_l: result.mcl_ug_l,
      exceedsMcl: result.initial_concentration_ug_l > result.mcl_ug_l,
      relativeLength: calculateRelativePlumeLength(props),
      retardationFactor: props.retardation_factor,
      halfLifeDays: props.half_life_days,
      // NEW: Include actual polygon for rendering
      polygon,
      sourceType,
    };
  });

  // Sort by plume area/length (longest first for proper rendering order)
  // This ensures longer plumes are rendered behind shorter ones
  plumes.sort((a, b) => b.lengthFt - a.lengthFt);

  return plumes;
}

/**
 * Calculate time-specific plume dimensions for a compound.
 * As time progresses, plumes migrate downgradient at different rates.
 *
 * @param compound - Compound properties
 * @param baseLengthFt - Base length at current time
 * @param seepageVelocityFtDay - Groundwater seepage velocity
 * @param timeDays - Time elapsed
 */
export function calculateTimeDependentPlumeLength(
  compound: CompoundPlumeParameters,
  baseLengthFt: number,
  seepageVelocityFtDay: number,
  timeDays: number
): number {
  // Contaminant velocity = seepage velocity / retardation factor
  const contaminantVelocity = seepageVelocityFtDay / compound.retardation_factor;

  // Migration distance
  const migrationFt = contaminantVelocity * timeDays;

  // Plume length grows with migration (simplified model)
  // More accurate would use advection-dispersion equation
  const growthFactor = 1 + (migrationFt / baseLengthFt) * 0.3;

  return Math.round(baseLengthFt * growthFactor);
}

// ============================================================================
// COLOR UTILITIES
// ============================================================================

/**
 * Get compound color with optional opacity adjustment.
 */
export function getCompoundColor(compoundName: string, opacity: number = 1): string {
  const props = getCompoundProperties(compoundName);
  const baseColor = props.plume_color;

  if (opacity >= 1) return baseColor;

  // Convert hex to rgba
  const r = parseInt(baseColor.slice(1, 3), 16);
  const g = parseInt(baseColor.slice(3, 5), 16);
  const b = parseInt(baseColor.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Standard compound colors for the legend.
 * MUST match COMPOUND_PARAMETERS in polygon.ts for consistency across 2D, 3D, and legends.
 */
export const COMPOUND_COLORS: Record<string, string> = {
  'Total BTEX': '#06b6d4',   // Cyan
  'Total TPH': '#6b7280',    // Gray
  'Benzene': '#3b82f6',      // Blue
  'Toluene': '#22c55e',      // Green
  'Ethylbenzene': '#f59e0b', // Amber
  'Xylenes': '#a855f7',      // Purple
  'C6-C12': '#f97316',       // Orange (gasoline range)
  'C12-C28': '#854d0e',      // Brown (diesel range)
  'C28-C35': '#78716c',      // Stone gray (motor oil range)
};

// ============================================================================
// EXPORT DEFAULT
// ============================================================================

export default {
  getCompoundProperties,
  calculateRelativePlumeLength,
  calculateCompoundPlumeLength,
  generateMultiContaminantPlumes,
  calculateTimeDependentPlumeLength,
  getCompoundColor,
  COMPOUND_COLORS,
};
